import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Radio, Loader2, RefreshCcw, Zap, ZapOff, Play, Pause, Clock, MessageSquare,
  FileText, HistoryIcon, ExternalLink, AlertTriangle, Settings2, Send, Save,
  RotateCcw, Eye, Search, X, Plus, Trash2, ChevronDown, ChevronRight, Copy,
  User, FileSignature, Info,
} from "lucide-react";

const EVENT_COLOR = {
  "client.created":    "text-cyan-300 border-cyan-500/40 bg-cyan-500/5",
  "contract.created":  "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
  "payment.interest":  "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5",
  "contract.renewed":  "text-purple-300 border-purple-500/40 bg-purple-500/5",
  "contract.settled":  "text-emerald-300 border-emerald-500/40 bg-emerald-500/5",
  "before_due.1d":     "text-blue-300 border-blue-500/40 bg-blue-500/5",
  "on_due_date":       "text-amber-300 border-amber-500/40 bg-amber-500/5",
  "overdue.1d":        "text-orange-300 border-orange-500/40 bg-orange-500/5",
  "overdue.3d":        "text-red-300 border-red-500/40 bg-red-500/5",
  "overdue.7d":        "text-red-300 border-red-500/40 bg-red-500/5",
  "recovery.entered":  "text-red-300 border-red-500/40 bg-red-500/5",
};

const CATEGORY_LABELS = {
  operacional: "Operacional",
  cobranca: "Cobrança",
  recuperacao: "Recuperação",
  marketing: "Marketing",
  manual: "Manual",
  // legadas (compat)
  avisos: "Avisos (legado)",
  acordos: "Acordos (legado)",
};

const CATEGORY_OPTIONS = ["operacional", "cobranca", "recuperacao", "marketing", "manual"];

export default function ComunicacaoPage() {
  const [tab, setTab] = useState("templates");
  return (
    <Layout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-300">Administração · V2</div>
          <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1 flex items-center gap-2" data-testid="comunicacao-title">
            <Radio size={26} className="text-emerald-400"/> Centro de Comunicação
          </h1>
          <p className="text-[#A3A3A3] text-[13px] sm:text-sm mt-1.5 max-w-2xl">
            Todas as mensagens automáticas e manuais em um só lugar.
            <strong className="text-amber-300"> Automações desligadas por padrão</strong> —
            você ativa cada regra quando quiser.
          </p>
        </div>
        <Link to="/integracoes/whatsapp_evolution" className="fc-btn-ghost text-xs flex items-center gap-1.5 whitespace-nowrap" data-testid="link-integ-evolution">
          <ExternalLink size={12}/> Configuração Evolution
        </Link>
      </div>

      <div className="flex gap-1 mb-5 border-b border-[#1a1a1a] overflow-x-auto" data-testid="com-tabs">
        {[
          { v: "templates", l: "Templates",         icon: FileText },
          { v: "test",      l: "Testar & enviar",   icon: Send },
          { v: "rules",     l: "Regras",            icon: Zap },
          { v: "vars",      l: "Variáveis",         icon: Settings2 },
          { v: "history",   l: "Histórico",         icon: HistoryIcon },
        ].map(t => {
          const TIcon = t.icon;
          return (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              className={`px-3.5 py-2 text-[12.5px] flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${tab === t.v ? "border-emerald-400 text-emerald-300" : "border-transparent text-[#A3A3A3] hover:text-[#E5E5E5]"}`}
              data-testid={`com-tab-${t.v}`}
            >
              <TIcon size={13}/> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "templates" && <TemplatesTab/>}
      {tab === "test"      && <TestSendTab/>}
      {tab === "rules"     && <RulesTab/>}
      {tab === "vars"      && <VariablesTab/>}
      {tab === "history"   && <HistoryTab/>}
    </Layout>
  );
}

// ==================== TEMPLATES (editor visual) ====================
function TemplatesTab() {
  const [items, setItems] = useState([]);
  const [vars, setVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // template em edição
  const [preview, setPreview] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const textareaRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [tpls, v] = await Promise.all([
        api.get("/whatsapp/templates"),
        api.get("/comunicacao/variables"),
      ]);
      setItems(tpls.data);
      setVars(v.data.filter(x => !x.label.startsWith("(legado)")));
      // preserva seleção
      if (selected) {
        const updated = tpls.data.find(t => t.id === selected.id);
        if (updated && !dirty) setSelected(updated);
      } else if (tpls.data.length) {
        setSelected(tpls.data[0]);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const doPreview = async (content) => {
    if (!content) { setPreview(""); return; }
    try {
      const { data } = await api.post("/comunicacao/render", { content });
      setPreview(data.rendered);
    } catch { setPreview(""); }
  };

  useEffect(() => { if (selected) doPreview(selected.content); /* eslint-disable-next-line */ }, [selected?.id]);

  const insertVar = (v) => {
    if (!selected || !textareaRef.current) return;
    const ta = textareaRef.current;
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const before = selected.content.slice(0, start);
    const after = selected.content.slice(end);
    const inserted = `{${v.var}}`;
    const newContent = before + inserted + after;
    setSelected({ ...selected, content: newContent });
    setDirty(true);
    // recoloca cursor após o insert
    setTimeout(() => {
      ta.focus();
      const pos = start + inserted.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const payload = {
        code: selected.code,
        name: selected.name,
        category: selected.category,
        content: selected.content,
        active: selected.active !== false,
        require_confirmation: !!selected.require_confirmation,
      };
      const { data } = await api.put(`/whatsapp/templates/${selected.id}`, payload);
      toast.success("Template salvo");
      setSelected(data);
      setDirty(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const resetOne = async () => {
    if (!selected?.is_system) return;
    if (!window.confirm(`Restaurar o conteúdo padrão do template "${selected.name}"? Sua edição atual será substituída.`)) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/whatsapp/templates/${selected.id}/reset`);
      toast.success("Template restaurado ao padrão");
      setSelected(data);
      setDirty(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao restaurar");
    } finally { setResetting(false); }
  };

  const duplicate = async () => {
    if (!selected) return;
    try {
      const { data } = await api.post(`/whatsapp/templates/${selected.id}/duplicate`);
      toast.success(`Duplicado como "${data.name}"`);
      await load();
      setSelected(data);
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao duplicar");
    }
  };

  const createNew = async () => {
    const code = window.prompt("Código único do novo template (só letras minúsculas, números e '_'):");
    if (!code) return;
    const name = window.prompt("Nome amigável:", code);
    if (!name) return;
    try {
      const { data } = await api.post("/whatsapp/templates", {
        code: code.trim().toLowerCase(),
        name,
        category: "operacional",
        content: "Olá, {nome_cliente}!\n\n",
        active: true,
      });
      toast.success("Template criado");
      await load();
      setSelected(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar");
    }
  };

  const delTemplate = async () => {
    if (!selected || selected.is_system) return;
    if (!window.confirm(`Excluir o template "${selected.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/whatsapp/templates/${selected.id}`);
      toast.success("Template excluído");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir");
    }
  };

  if (loading) return <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={16}/></div>;

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4" data-testid="templates-tab">
      {/* Lista */}
      <div className="fc-card p-0 overflow-hidden">
        <div className="p-3 border-b border-[#1a1a1a] flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-[#737373]">Templates ({items.length})</div>
          <button onClick={createNew} className="text-emerald-300 hover:text-emerald-200" title="Novo template" data-testid="tpl-new"><Plus size={14}/></button>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          {items.map(t => (
            <button
              key={t.id}
              onClick={() => { if (dirty && !window.confirm("Você tem alterações não salvas. Trocar de template mesmo assim?")) return; setSelected(t); setDirty(false); }}
              className={`w-full text-left px-3 py-2.5 border-b border-[#1a1a1a] hover:bg-[#0e0e0e] transition-colors ${selected?.id === t.id ? "bg-emerald-500/5 border-l-2 border-l-emerald-400" : ""}`}
              data-testid={`tpl-item-${t.code}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] text-[#E5E5E5] font-medium truncate">{t.name}</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {t.require_confirmation && <span title="Exige confirmação antes do envio automático" className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">conf.</span>}
                  {t.is_system && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30">sistema</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <code className="text-[10.5px] text-[#737373] font-mono-tab">{t.code}</code>
                <span className="text-[10px] text-[#A3A3A3]">· {CATEGORY_LABELS[t.category] || t.category}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      {selected ? (
        <div className="space-y-3" data-testid="tpl-editor">
          <div className="fc-card p-4">
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-[10.5px] uppercase text-[#737373]">Nome</label>
                <input className="fc-input text-[13px]" value={selected.name} onChange={e => { setSelected({ ...selected, name: e.target.value }); setDirty(true); }} data-testid="tpl-name"/>
              </div>
              <div>
                <label className="text-[10.5px] uppercase text-[#737373]">Categoria</label>
                <select className="fc-input text-[13px]" value={selected.category} onChange={e => { setSelected({ ...selected, category: e.target.value }); setDirty(true); }} data-testid="tpl-category">
                  {CATEGORY_OPTIONS.map(v => <option key={v} value={v}>{CATEGORY_LABELS[v]}</option>)}
                  {selected.category && !CATEGORY_OPTIONS.includes(selected.category) && (
                    <option value={selected.category}>{CATEGORY_LABELS[selected.category] || selected.category}</option>
                  )}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="text-[10.5px] uppercase text-[#737373]">Conteúdo</label>
              <div className="text-[10px] text-[#737373]">Código: <code className="font-mono-tab text-[#A3A3A3]">{selected.code}</code></div>
            </div>
            <textarea
              ref={textareaRef}
              rows={9}
              className="fc-input text-[13px] font-mono-tab leading-relaxed"
              value={selected.content}
              onChange={e => { setSelected({ ...selected, content: e.target.value }); setDirty(true); }}
              onBlur={() => doPreview(selected.content)}
              data-testid="tpl-content"
              placeholder="Escreva a mensagem. Use os botões abaixo para inserir variáveis..."
            />
            <div className="mt-3">
              <div className="text-[10.5px] uppercase text-[#737373] mb-1.5">Inserir variável</div>
              <div className="flex flex-wrap gap-1.5" data-testid="tpl-vars">
                {vars.map(v => (
                  <button
                    key={v.var}
                    onClick={() => insertVar(v)}
                    className="text-[11px] px-2 py-1 rounded border border-[#3a3a3a] bg-[#0a0a0a] hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition-colors flex items-center gap-1"
                    title={v.label}
                    data-testid={`tpl-var-${v.var}`}
                  >
                    <code className="font-mono-tab">{`{${v.var}}`}</code>
                  </button>
                ))}
              </div>
            </div>

            {/* Opções avançadas */}
            <div className="mt-4 pt-4 border-t border-[#1a1a1a] space-y-2.5">
              <label className="flex items-start gap-2 cursor-pointer text-[12.5px]" data-testid="tpl-require-conf-wrapper">
                <input
                  type="checkbox"
                  checked={!!selected.require_confirmation}
                  onChange={e => { setSelected({ ...selected, require_confirmation: e.target.checked }); setDirty(true); }}
                  className="mt-0.5 h-4 w-4 rounded border-[#3a3a3a] bg-[#0a0a0a] text-emerald-500 focus:ring-emerald-500/40 focus:ring-2 focus:ring-offset-0"
                  data-testid="tpl-require-confirmation"
                />
                <div className="flex-1">
                  <div className="text-[#E5E5E5] font-medium">Obrigatório confirmar antes de enviar</div>
                  <div className="text-[11px] text-[#A3A3A3] mt-0.5">
                    Mesmo em envios automáticos, a mensagem ficará em <code className="text-amber-300">awaiting_confirmation</code> até que um operador confirme.
                  </div>
                </div>
              </label>
              <div className="text-[11px] text-[#737373] flex items-start gap-2 pt-1">
                <Info size={11} className="mt-0.5 text-[#737373]"/>
                <div>
                  <strong className="text-[#A3A3A3]">Anexos</strong> (imagens, PDFs, áudio):
                  arquitetura pronta no backend — interface de upload será liberada em breve.
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="fc-card p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10.5px] uppercase text-[#737373] flex items-center gap-1.5"><Eye size={11}/> Pré-visualização (com dados de exemplo)</div>
              <button onClick={() => doPreview(selected.content)} className="text-[11px] text-[#A3A3A3] hover:text-emerald-300 flex items-center gap-1"><RefreshCcw size={11}/> Recarregar</button>
            </div>
            <div className="rounded-md border border-emerald-500/25 bg-emerald-500/5 p-3 text-[12.5px] text-emerald-100 whitespace-pre-wrap min-h-[80px]" data-testid="tpl-preview">
              {preview || <span className="text-[#737373] italic">— pré-visualização aparecerá aqui —</span>}
            </div>
          </div>

          {/* Ações */}
          <div className="fc-card p-3 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={duplicate} className="fc-btn-ghost text-xs flex items-center gap-1.5" data-testid="tpl-duplicate">
                <Copy size={12}/> Duplicar
              </button>
              {selected.is_system ? (
                <button onClick={resetOne} disabled={resetting} className="fc-btn-ghost text-xs flex items-center gap-1.5" data-testid="tpl-reset">
                  {resetting ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>} Restaurar padrão
                </button>
              ) : (
                <button onClick={delTemplate} className="text-xs px-3 py-1.5 rounded-md border border-red-500/30 text-red-300 hover:bg-red-500/10 flex items-center gap-1.5" data-testid="tpl-delete">
                  <Trash2 size={12}/> Excluir
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {dirty && <span className="text-[10.5px] text-amber-300 flex items-center gap-1"><AlertTriangle size={11}/> alterações não salvas</span>}
              <button onClick={save} disabled={saving || !dirty} className="fc-btn-primary text-xs flex items-center gap-1.5" data-testid="tpl-save">
                {saving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Salvar template
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="fc-card p-8 text-center text-[#737373]">Selecione um template ao lado para editar.</div>
      )}
    </div>
  );
}

// ==================== TESTE / ENVIO MANUAL ====================
function TestSendTab() {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [contract, setContract] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateCode, setTemplateCode] = useState("");
  const [content, setContent] = useState("");
  const [rendered, setRendered] = useState("");
  const [phoneTo, setPhoneTo] = useState("");
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/whatsapp/templates", { params: { active: true } }).then(r => setTemplates(r.data));
    api.get("/whatsapp/config/default").then(r => setConfig(r.data)).catch(() => {});
  }, []);

  const doSearch = async (term) => {
    setQ(term);
    if (term.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/clients", { params: { search: term, limit: 8 } });
      const items = data.items || data;
      setResults(items || []);
    } finally { setSearching(false); }
  };

  const selectClient = async (c) => {
    setClient(c);
    setResults([]);
    setQ(c.full_name || c.name);
    // buscar contratos do cliente
    try {
      const { data } = await api.get(`/clients/${c.id}/contract`).catch(() => ({ data: null }));
      const arr = [];
      if (data) {
        if (Array.isArray(data)) arr.push(...data);
        else if (data.id) arr.push(data);
      }
      setContracts(arr);
      setContract(arr[0] || null);
    } catch {
      setContracts([]);
      setContract(null);
    }
    setPhoneTo(c.whatsapp || c.phone || "");
  };

  const doPreview = async () => {
    if (!client) { toast.error("Selecione um cliente"); return; }
    setPreviewLoading(true);
    try {
      const payload = { client_id: client.id };
      if (contract?.id) {
        payload.contract_id = contract.id;
        payload.contract_type = contract.contract_type || (contract.total_installments ? "installment_contract" : "contract");
      }
      if (templateCode) payload.template_code = templateCode;
      else if (content) payload.content = content;
      else { toast.error("Escolha um template ou escreva um conteúdo"); return; }
      const { data } = await api.post("/comunicacao/preview", payload);
      setContent(data.content);
      setRendered(data.rendered);
      if (data.phone_to && !phoneTo) setPhoneTo(data.phone_to);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no preview");
    } finally { setPreviewLoading(false); }
  };

  // preview automático ao mudar template
  useEffect(() => {
    if (templateCode && client) doPreview();
    // eslint-disable-next-line
  }, [templateCode, contract?.id]);

  const doSend = async () => {
    if (!client) return toast.error("Selecione um cliente");
    if (!rendered || rendered.trim().length < 3) return toast.error("Gere ou digite o conteúdo antes de enviar");
    if (!phoneTo) return toast.error("Informe o número de destino");
    setSending(true);
    try {
      const payload = {
        client_id: client.id,
        content: rendered,  // envia o texto JÁ RENDERIZADO (permite edição)
        phone_to: phoneTo,
        origin: "manual",
        template_code: templateCode || null,
      };
      if (contract?.id) {
        payload.contract_id = contract.id;
        payload.contract_type = contract.contract_type || (contract.total_installments ? "installment_contract" : "contract");
      }
      const { data } = await api.post("/whatsapp/send", payload);
      toast.success(`Mensagem enviada (status: ${data.status})`);
      // limpa
      setRendered("");
      setContent("");
      setTemplateCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar");
    } finally { setSending(false); }
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-4" data-testid="test-tab">
      {/* Coluna esquerda: cliente + template */}
      <div className="space-y-3">
        <div className="fc-card p-4">
          <div className="text-[10.5px] uppercase text-[#737373] mb-2 flex items-center gap-1.5"><User size={11}/> 1. Cliente</div>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-[#737373]"/>
            <input
              className="fc-input pl-8 text-[13px]"
              placeholder="Buscar cliente por nome, CPF, telefone..."
              value={q}
              onChange={e => doSearch(e.target.value)}
              data-testid="test-client-search"
            />
            {client && (
              <button onClick={() => { setClient(null); setContract(null); setContracts([]); setRendered(""); setPhoneTo(""); setQ(""); }} className="absolute right-2 top-2 text-[#737373] hover:text-red-300"><X size={13}/></button>
            )}
            {results.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 fc-card p-1 max-h-64 overflow-y-auto shadow-xl">
                {results.map(r => (
                  <button key={r.id} onClick={() => selectClient(r)} className="w-full text-left px-2 py-1.5 rounded hover:bg-emerald-500/10 text-[12.5px]" data-testid={`test-client-opt-${r.id}`}>
                    <div className="text-[#E5E5E5]">{r.full_name || r.name}</div>
                    <div className="text-[10.5px] text-[#737373] font-mono-tab">{r.cpf} · {r.whatsapp || r.phone || "sem tel"}</div>
                  </button>
                ))}
              </div>
            )}
            {searching && <div className="absolute right-8 top-2.5"><Loader2 size={13} className="animate-spin text-[#737373]"/></div>}
          </div>
          {client && (
            <div className="mt-3 p-2.5 rounded-md bg-emerald-500/5 border border-emerald-500/25 text-[12px]" data-testid="test-client-selected">
              <div className="text-emerald-200 font-medium">{client.full_name || client.name}</div>
              <div className="text-[11px] text-[#A3A3A3] font-mono-tab">{client.cpf || "—"} · {client.whatsapp || client.phone || "sem WhatsApp"}</div>
            </div>
          )}
        </div>

        {contracts.length > 0 && (
          <div className="fc-card p-4">
            <div className="text-[10.5px] uppercase text-[#737373] mb-2 flex items-center gap-1.5"><FileSignature size={11}/> Contrato (opcional)</div>
            <select className="fc-input text-[13px]" value={contract?.id || ""} onChange={e => setContract(contracts.find(c => c.id === e.target.value) || null)} data-testid="test-contract-select">
              <option value="">— sem contrato —</option>
              {contracts.map(c => (
                <option key={c.id} value={c.id}>{c.code || c.id.slice(0,8)} · R$ {(c.capital || 0).toFixed(2)}</option>
              ))}
            </select>
          </div>
        )}

        <div className="fc-card p-4">
          <div className="text-[10.5px] uppercase text-[#737373] mb-2 flex items-center gap-1.5"><FileText size={11}/> 2. Template</div>
          <select className="fc-input text-[13px]" value={templateCode} onChange={e => setTemplateCode(e.target.value)} data-testid="test-template-select">
            <option value="">— sem template (texto livre) —</option>
            {templates.map(t => (
              <option key={t.code} value={t.code}>[{CATEGORY_LABELS[t.category] || t.category}] {t.name}</option>
            ))}
          </select>
          {templateCode && (
            <div className="mt-2 text-[11px] text-[#A3A3A3] p-2 rounded bg-[#0a0a0a] border border-[#1f1f1f]">
              <div className="text-[10px] uppercase text-[#737373] mb-0.5">Original</div>
              <div className="whitespace-pre-wrap text-[11.5px]">{content || <span className="italic text-[#737373]">Carregando…</span>}</div>
            </div>
          )}
        </div>

        <div className="fc-card p-4">
          <div className="text-[10.5px] uppercase text-[#737373] mb-2 flex items-center gap-1.5"><MessageSquare size={11}/> 3. Número destino</div>
          <input
            className="fc-input text-[13px] font-mono-tab"
            value={phoneTo}
            onChange={e => setPhoneTo(e.target.value)}
            placeholder="5511999999999"
            data-testid="test-phone"
          />
          <div className="text-[10.5px] text-[#737373] mt-1">Somente números — formato E.164 sem +</div>
        </div>
      </div>

      {/* Coluna direita: preview editável + envio */}
      <div className="space-y-3">
        {!config && (
          <div className="p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-[12px] flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
            <div>Nenhum WhatsApp conectado. Vá em <Link to="/integracoes/whatsapp_evolution" className="underline">Integrações → Evolution</Link> para conectar.</div>
          </div>
        )}
        {config && (
          <div className="text-[11px] text-[#A3A3A3] px-1">
            Enviando por <span className="font-mono-tab text-emerald-300">+{config.phone_number}</span>
            {config.phone_label && <> ({config.phone_label})</>} · provider <span className="text-[#737373]">{config.provider}</span>
          </div>
        )}

        <div className="fc-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10.5px] uppercase text-[#737373] flex items-center gap-1.5"><Eye size={11}/> Preview — você pode editar antes de enviar</div>
            <button onClick={doPreview} disabled={!client || previewLoading} className="text-[11px] text-emerald-300 hover:text-emerald-200 flex items-center gap-1 disabled:opacity-40" data-testid="test-preview-btn">
              {previewLoading ? <Loader2 size={11} className="animate-spin"/> : <RefreshCcw size={11}/>} Recalcular preview
            </button>
          </div>
          <textarea
            rows={10}
            className="fc-input text-[13px] leading-relaxed"
            value={rendered}
            onChange={e => setRendered(e.target.value)}
            placeholder="Escolha um cliente + template e o texto renderizado aparecerá aqui. Você pode editar livremente antes de enviar."
            data-testid="test-rendered"
          />
          <div className="mt-1 text-[10.5px] text-[#737373]">{rendered.length} caracteres</div>
        </div>

        <div className="fc-card p-3 flex items-center justify-end gap-2">
          <button
            onClick={doSend}
            disabled={sending || !client || !rendered || !phoneTo || !config}
            className="fc-btn-primary text-xs flex items-center gap-1.5"
            data-testid="test-send-btn"
          >
            {sending ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
            Enviar via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== REGRAS ====================
function RulesTab() {
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        api.get("/comunicacao/rules"),
        api.get("/whatsapp/templates", { params: { active: true } }),
      ]);
      setRules(r.data);
      setTemplates(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try {
      await api.patch(`/comunicacao/rules/${r.id}/toggle`);
      toast[r.active ? "warning" : "success"](
        r.active ? "Regra desligada" : "Regra ativada — envios automáticos serão iniciados"
      );
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha"); }
  };

  const saveRule = async (r, patch) => {
    try {
      await api.put(`/comunicacao/rules/${r.id}`, { ...r, ...patch });
      toast.success("Regra atualizada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha"); }
  };

  if (loading) return <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={16}/></div>;

  const activeCount = rules.filter(r => r.active).length;

  return (
    <div>
      <div className="mb-4 p-3 rounded-md border border-amber-500/30 bg-amber-500/5 text-[12px] text-amber-200 flex items-start gap-2" data-testid="rules-warning">
        <AlertTriangle size={14} className="flex-shrink-0 mt-0.5"/>
        <div>
          <strong>{activeCount} de {rules.length} regras ativas.</strong>{" "}
          Ativação envia mensagens automaticamente quando os eventos ocorrem.
          Verifique cuidadosamente antes de ligar (sugestão: teste primeiro em modo manual).
        </div>
      </div>

      <div className="grid gap-2" data-testid="rules-list">
        {rules.map(r => (
          <div key={r.id} className="fc-card p-4" data-testid={`rule-${r.code}`}>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10.5px] px-2 py-0.5 rounded border ${EVENT_COLOR[r.event] || "border-[#3a3a3a] text-[#A3A3A3]"}`}>
                    {r.event_label || r.event}
                  </span>
                  {r.active ? (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 flex items-center gap-1">
                      <Zap size={9}/> ATIVA
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-[#0a0a0a] text-[#737373] border border-[#3a3a3a] flex items-center gap-1">
                      <ZapOff size={9}/> desligada
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-[#A3A3A3] mt-1 leading-snug">{r.event_description}</div>
                <div className="mt-3 flex gap-3 flex-wrap items-center text-[11.5px]">
                  <label className="flex items-center gap-1.5">
                    <span className="text-[#737373]">Template:</span>
                    <select
                      className="fc-input text-[11.5px] py-1"
                      value={r.template_code || ""}
                      onChange={e => saveRule(r, { template_code: e.target.value || null })}
                      data-testid={`rule-template-${r.code}`}
                    >
                      <option value="">— nenhum —</option>
                      {templates.map(t => <option key={t.code} value={t.code}>{t.name} ({t.category})</option>)}
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5">
                    <Clock size={11} className="text-[#737373]"/>
                    <input
                      type="time"
                      className="fc-input text-[11.5px] py-1 w-24"
                      value={r.schedule_time || ""}
                      onChange={e => saveRule(r, { schedule_time: e.target.value || null })}
                      data-testid={`rule-time-${r.code}`}
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-[#737373]">Modo:</span>
                    <select
                      className="fc-input text-[11.5px] py-1"
                      value={r.mode || "auto"}
                      onChange={e => saveRule(r, { mode: e.target.value })}
                      data-testid={`rule-mode-${r.code}`}
                    >
                      <option value="auto">Automático</option>
                      <option value="manual">Manual</option>
                    </select>
                  </label>
                </div>
              </div>
              <button
                onClick={() => toggle(r)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-md border text-xs flex items-center gap-1.5 ${
                  r.active
                    ? "border-red-500/40 text-red-300 hover:bg-red-500/10"
                    : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                }`}
                data-testid={`rule-toggle-${r.code}`}
              >
                {r.active ? <><Pause size={12}/> Desligar</> : <><Play size={12}/> Ativar</>}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== VARIÁVEIS ====================
function VariablesTab() {
  const [vars, setVars] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/comunicacao/variables").then(r => setVars(r.data)).finally(() => setLoading(false));
  }, []);
  const primary = vars.filter(v => !v.label.startsWith("(legado)"));
  const legacy = vars.filter(v => v.label.startsWith("(legado)"));
  const copyVar = (v) => {
    navigator.clipboard?.writeText(`{${v.var}}`);
    toast.success(`{${v.var}} copiado`);
  };
  return (
    <div>
      <div className="text-[12px] text-[#A3A3A3] mb-3">
        Variáveis disponíveis. Use <code className="text-emerald-300">{"{nome_cliente}"}</code> nos templates — o sistema também aceita <code className="text-emerald-300">{"{{nome_cliente}}"}</code>.
      </div>
      {loading ? (
        <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={16}/></div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-2 mb-6" data-testid="vars-primary">
            {primary.map(v => (
              <button
                key={v.var}
                onClick={() => copyVar(v)}
                className="fc-card p-3 flex items-center gap-3 hover:bg-[#0e0e0e] hover:border-[#D4AF37]/40 transition-colors text-left"
                title="Clique para copiar"
              >
                <code className="text-[12px] font-mono-tab text-[#D4AF37] flex-shrink-0">{`{${v.var}}`}</code>
                <div className="text-[12px] text-[#E5E5E5] flex-1">{v.label}</div>
                <Copy size={11} className="text-[#737373] flex-shrink-0"/>
              </button>
            ))}
          </div>
          {legacy.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#737373] mb-2">Legado (V1 — mantidas por compatibilidade)</div>
              <div className="grid md:grid-cols-3 gap-2" data-testid="vars-legacy">
                {legacy.map(v => (
                  <div key={v.var} className="fc-card p-2.5 flex items-center gap-2 opacity-70">
                    <code className="text-[11px] font-mono-tab text-[#737373]">{`{${v.var}}`}</code>
                    <div className="text-[11px] text-[#A3A3A3]">{v.label.replace("(legado) ", "")}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ==================== HISTÓRICO ====================
function HistoryTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  // filtros
  const [q, setQ] = useState("");
  const [origin, setOrigin] = useState("");
  const [status, setStatus] = useState("");
  const [templateCode, setTemplateCode] = useState("");
  const [indicatorId, setIndicatorId] = useState("");
  const [socioId, setSocioId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // catálogos
  const [templates, setTemplates] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [socios, setSocios] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/whatsapp/templates"),
      api.get("/indicators").catch(() => ({ data: [] })),
      api.get("/socios").catch(() => ({ data: [] })),
    ]).then(([t, i, s]) => {
      setTemplates(t.data || []);
      const inds = Array.isArray(i.data) ? i.data : (i.data?.items || []);
      const scs = Array.isArray(s.data) ? s.data : (s.data?.items || []);
      setIndicators(inds);
      setSocios(scs);
    });
  }, []);

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (q) params.q = q;
    if (origin) params.origin = origin;
    if (status) params.status = status;
    if (templateCode) params.template_code = templateCode;
    if (indicatorId) params.indicator_id = indicatorId;
    if (socioId) params.socio_id = socioId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/whatsapp/history", { params })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [origin, status, templateCode, indicatorId, socioId, dateFrom, dateTo]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 350); // debounce da busca livre
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const clearAll = () => {
    setQ(""); setOrigin(""); setStatus(""); setTemplateCode("");
    setIndicatorId(""); setSocioId(""); setDateFrom(""); setDateTo("");
  };

  const confirmSend = async (m) => {
    if (!window.confirm("Confirmar envio desta mensagem via WhatsApp agora?")) return;
    try {
      await api.post(`/whatsapp/messages/${m.id}/confirm-send`);
      toast.success("Envio confirmado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao confirmar"); }
  };
  const cancelSend = async (m) => {
    if (!window.confirm("Cancelar esta mensagem? Ela não será enviada.")) return;
    try {
      await api.post(`/whatsapp/messages/${m.id}/cancel`);
      toast.success("Mensagem cancelada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Falha ao cancelar"); }
  };

  const fmt = (iso) => (iso || "").replace("T", " ").slice(0, 19);
  const statusPill = (s) => {
    const map = {
      read:                    { l: "Lido",             c: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5" },
      delivered:               { l: "Entregue",         c: "text-blue-300 border-blue-500/40 bg-blue-500/5" },
      sent:                    { l: "Enviado",          c: "text-cyan-300 border-cyan-500/40 bg-cyan-500/5" },
      pending:                 { l: "Pendente",         c: "text-[#A3A3A3] border-[#3a3a3a] bg-[#0a0a0a]" },
      awaiting_confirmation:   { l: "Aguarda conf.",    c: "text-amber-300 border-amber-500/40 bg-amber-500/5" },
      error:                   { l: "Erro",             c: "text-red-300 border-red-500/40 bg-red-500/5" },
    };
    const m = map[s] || map.pending;
    return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.c}`}>{m.l}</span>;
  };

  const activeFilters = [q, origin, status, templateCode, indicatorId, socioId, dateFrom, dateTo].filter(Boolean).length;

  return (
    <div>
      {/* Filtros */}
      <div className="fc-card p-3 mb-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="col-span-2 md:col-span-2 relative">
            <Search size={12} className="absolute left-2.5 top-2.5 text-[#737373]"/>
            <input className="fc-input pl-7 text-xs" placeholder="Buscar por cliente ou telefone..." value={q} onChange={e => setQ(e.target.value)} data-testid="history-q"/>
          </div>
          <select className="fc-input text-xs" value={indicatorId} onChange={e => setIndicatorId(e.target.value)} data-testid="history-indicator">
            <option value="">Todos indicadores</option>
            {indicators.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <select className="fc-input text-xs" value={socioId} onChange={e => setSocioId(e.target.value)} data-testid="history-socio">
            <option value="">Todos sócios</option>
            {socios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="fc-input text-xs" value={templateCode} onChange={e => setTemplateCode(e.target.value)} data-testid="history-template">
            <option value="">Qualquer template</option>
            {templates.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
          <select className="fc-input text-xs" value={origin} onChange={e => setOrigin(e.target.value)} data-testid="history-origin-filter">
            <option value="">Toda origem</option>
            <option value="manual">Manual</option>
            <option value="automatic">Automática</option>
            <option value="bulk">Em massa</option>
          </select>
          <select className="fc-input text-xs" value={status} onChange={e => setStatus(e.target.value)} data-testid="history-status-filter">
            <option value="">Todo status</option>
            <option value="pending">Pendente</option>
            <option value="awaiting_confirmation">Aguardando conf.</option>
            <option value="sent">Enviado</option>
            <option value="delivered">Entregue</option>
            <option value="read">Lido</option>
            <option value="error">Erro</option>
          </select>
          <input type="date" className="fc-input text-xs" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Data inicial" data-testid="history-date-from"/>
          <input type="date" className="fc-input text-xs" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Data final" data-testid="history-date-to"/>
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#1a1a1a]">
          <div className="text-[11px] text-[#A3A3A3]">
            Total: <span className="font-mono-tab text-[#D4AF37]">{total}</span>
            {activeFilters > 0 && <span className="ml-2 text-emerald-300">· {activeFilters} filtro(s) ativo(s)</span>}
          </div>
          <div className="flex items-center gap-2">
            {activeFilters > 0 && (
              <button onClick={clearAll} className="text-[11px] text-[#A3A3A3] hover:text-red-300 flex items-center gap-1" data-testid="history-clear">
                <X size={11}/> Limpar filtros
              </button>
            )}
            <button onClick={load} className="fc-btn-ghost text-xs flex items-center gap-1"><RefreshCcw size={11}/> Atualizar</button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="fc-card p-8 text-center"><Loader2 className="animate-spin mx-auto" size={16}/></div>
      ) : !items.length ? (
        <div className="fc-card p-10 text-center text-[#737373]">Nenhuma comunicação encontrada com os filtros atuais.</div>
      ) : (
        <div className="space-y-1.5">
          {items.map(m => {
            const isOpen = !!expanded[m.id];
            return (
              <div key={m.id} className="fc-card p-0 overflow-hidden" data-testid={`history-item-${m.id}`}>
                <button
                  onClick={() => setExpanded({ ...expanded, [m.id]: !isOpen })}
                  className="w-full text-left px-3 py-2.5 hover:bg-[#0e0e0e] transition-colors grid grid-cols-[16px_160px_1fr_140px_90px_90px_130px] gap-3 items-center"
                >
                  {isOpen ? <ChevronDown size={13} className="text-[#737373]"/> : <ChevronRight size={13} className="text-[#737373]"/>}
                  <span className="font-mono-tab text-[11px] text-[#A3A3A3]">{fmt(m.created_at)}</span>
                  <span className="text-[12.5px] text-[#E5E5E5] truncate">{m.client_name || "—"}</span>
                  <span className="text-[11.5px] text-[#A3A3A3] truncate">{m.template_name || m.template_code || <span className="italic text-[#737373]">livre</span>}</span>
                  <span className="text-[11px] text-[#A3A3A3]">
                    {m.origin === "automatic" ? "Auto" : m.origin === "bulk" ? "Massa" : "Manual"}
                  </span>
                  <span>{statusPill(m.status)}</span>
                  <span className="text-[11px] text-[#A3A3A3] truncate">{m.sender_name || "Sistema"}</span>
                </button>
                {isOpen && (
                  <div className="border-t border-[#1a1a1a] p-3 bg-[#080808]" data-testid={`history-detail-${m.id}`}>
                    <div className="grid md:grid-cols-2 gap-3 text-[11.5px]">
                      <div>
                        <div className="text-[10px] uppercase text-[#737373] mb-1">Conteúdo enviado</div>
                        <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] p-2.5 whitespace-pre-wrap text-[#E5E5E5]">{m.content}</div>
                        {m.status === "awaiting_confirmation" && (
                          <div className="mt-3 flex gap-2">
                            <button onClick={() => confirmSend(m)} className="fc-btn-primary text-xs flex items-center gap-1.5" data-testid={`history-confirm-${m.id}`}>
                              <Send size={12}/> Confirmar envio
                            </button>
                            <button onClick={() => cancelSend(m)} className="text-xs px-3 py-1.5 rounded-md border border-red-500/30 text-red-300 hover:bg-red-500/10" data-testid={`history-cancel-${m.id}`}>
                              Cancelar
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <InfoRow label="ID mensagem" value={m.id} mono/>
                        <InfoRow label="Destino" value={m.phone_to} mono/>
                        <InfoRow label="Origem" value={m.phone_from} mono/>
                        <InfoRow label="Provider" value={m.provider}/>
                        <InfoRow label="Provider Msg ID" value={m.provider_message_id || "—"} mono/>
                        <InfoRow label="Enviado por" value={`${m.sender_name || "Sistema"} (${m.sender_role || "—"})`}/>
                        {m.confirmed_by_name && <InfoRow label="Confirmado por" value={`${m.confirmed_by_name} · ${fmt(m.confirmed_at)}`}/>}
                        <InfoRow label="Enviado em" value={fmt(m.sent_at) || "—"}/>
                        <InfoRow label="Entregue em" value={fmt(m.delivered_at) || "—"}/>
                        <InfoRow label="Lido em" value={fmt(m.read_at) || "—"}/>
                        <InfoRow label="Categoria" value={CATEGORY_LABELS[m.category] || m.category || "—"}/>
                        {m.error_message && (
                          <div>
                            <div className="text-[10px] uppercase text-red-300 mb-0.5">Erro</div>
                            <div className="text-[11px] text-red-200 p-2 rounded border border-red-500/30 bg-red-500/5">{m.error_message}</div>
                          </div>
                        )}
                        {m.provider_response && (
                          <div>
                            <div className="text-[10px] uppercase text-[#737373] mb-0.5 flex items-center gap-1"><Info size={10}/> Resposta Evolution</div>
                            <pre className="text-[10.5px] leading-tight p-2 rounded border border-[#1f1f1f] bg-[#0a0a0a] text-[#A3A3A3] overflow-x-auto max-h-40">{JSON.stringify(m.provider_response, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3 items-baseline">
      <span className="text-[10px] uppercase text-[#737373]">{label}</span>
      <span className={`text-[11.5px] text-[#A3A3A3] text-right truncate ${mono ? "font-mono-tab" : ""}`} title={String(value || "")}>{value || "—"}</span>
    </div>
  );
}
